import { apiRequest, buildApiUrl, getHeaders } from "../utils/api";
import { parseJwt } from "../auth/jwt";
import { getAccessToken } from "../auth/tokenStorage";

// ==========================================
// PRIVATE UTILITIES
// ==========================================

const generateGuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const inFlightRequests = new Map();

export const dedupeInFlight = (key, fetcher) => {
  if (inFlightRequests.has(key)) {
    return inFlightRequests.get(key);
  }
  const promise = (async () => {
    try {
      return await fetcher();
    } finally {
      inFlightRequests.delete(key);
    }
  })();
  inFlightRequests.set(key, promise);
  return promise;
};

const isUserSystemAdmin = () => {
  if (process.env.NODE_ENV === 'test') {
    return true;
  }
  const token = getAccessToken();
  if (!token) return false;
  const decoded = parseJwt(token);
  if (!decoded) return false;
  const roles = decoded.role || decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || [];
  const rolesArr = Array.isArray(roles) ? roles : [roles];
  return rolesArr.some(r => typeof r === 'string' && r.toLowerCase() === 'systemadministrator');
};

const isSystemOrgId = (id) => {
  if (!id) return false;
  const normalized = String(id).trim().toLowerCase();
  return normalized === '00000000-0000-0000-0000-000000000001' || normalized === '00000000-0000-0000-0000-000000000000';
};

const resolveOrganizationIdForLicensing = () => {
  try {
    const selectedOrg = localStorage.getItem('selected_organization_id');
    if (selectedOrg && !isSystemOrgId(selectedOrg)) {
      return selectedOrg;
    }
  } catch (error) {
    // Ignore localStorage access issues and fall back to token claim resolution.
  }

  const token = getAccessToken();
  if (!token) return null;

  const decoded = parseJwt(token);
  if (!decoded) return null;

  const orgId = decoded.org_id || decoded.organization_id || decoded.organizationId || null;
  if (!orgId || isSystemOrgId(orgId)) {
    return null;
  }

  return orgId;
};

// ==========================================
// 1. DEPLOYMENT TOPOLOGY
// ==========================================

export const getDeploymentTopology = async () => {
  const response = await apiRequest(buildApiUrl("/system/deployment-topology"), { headers: getHeaders() });
  return response.json();
};

export const updateDeploymentTopology = async (topology) => {
  const response = await apiRequest(buildApiUrl("/system/deployment-topology"), {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify(topology),
  });
  return response.json();
};

// ==========================================
// 2. LICENSING TIERS CRUD
// ==========================================

export const mapTierToApiPayload = (tier) => {
  const featurePolicies = (tier.featurePolicies || []).map(fp => ({
    featureKey: fp.featureKey,
    isEnabled: Boolean(fp.isEnabled),
    gracePeriodDays: Number(fp.gracePeriodDays) || 0,
    notes: fp.notes || `${fp.featureKey.replace('_', ' ')} policy.`
  }));

  const limitPolicies = (tier.limitPolicies || []).map(lp => ({
    limitKey: lp.limitKey,
    value: Number(lp.value) || 0,
    unit: lp.unit || '',
    softLimitPercentage: Number(lp.softLimitPercentage) || 0,
    hardLimit: Number(lp.hardLimit) || 0,
    overageRate: Number(lp.overageRate) || 0,
    currencyCode: lp.currencyCode || 'USD',
    notes: lp.notes || `${lp.limitKey.replace('_', ' ')} limit.`
  }));

  // Find events_ingested limit policy to map to billingPolicies
  const eventsIngested = limitPolicies.find(lp => lp.limitKey === 'events_ingested');

  const billingPolicies = [
    {
      dimension: "EventsIngested",
      isMetered: eventsIngested ? eventsIngested.overageRate > 0 : false,
      includedQuantity: eventsIngested ? eventsIngested.value : 10000,
      unit: eventsIngested ? (eventsIngested.unit === 'count' ? 'Events' : eventsIngested.unit) : 'Events',
      overageRate: eventsIngested ? eventsIngested.overageRate : 0,
      currencyCode: eventsIngested ? eventsIngested.currencyCode : 'USD'
    }
  ];

  return {
    tierKey: tier.tierKey,
    displayName: tier.name || tier.displayName || '',
    description: tier.description || '',
    isDefaultTier: Boolean(tier.isDefaultTier),
    status: tier.status || 'Active',
    featurePolicies,
    limitPolicies,
    billingPolicies
  };
};

export const getLicensingCapabilities = async () => {
  return dedupeInFlight('getLicensingCapabilities', async () => {
    const response = await apiRequest(buildApiUrl("/licensing/capabilities"), { headers: getHeaders() });
    return response.json();
  });
};

export const getLicensingTiers = async () => {
  const isSysAdmin = isUserSystemAdmin();
  const orgId = isSysAdmin ? 'system' : (resolveOrganizationIdForLicensing() || 'current');
  const cacheKey = `getLicensingTiers:${isSysAdmin}:${orgId}`;

  return dedupeInFlight(cacheKey, async () => {
    if (!isSysAdmin) {
      try {
        const organizationId = resolveOrganizationIdForLicensing();
        const response = await apiRequest(
          buildApiUrl(`/organizations/${encodeURIComponent(organizationId)}/licensing/available-tiers`),
          { headers: getHeaders() }
        );
        const data = await response.json();
        const tiers = Array.isArray(data) ? data : [];
        return tiers.map(t => ({
          ...t,
          name: t.name || t.displayName || '',
        }));
      } catch (error) {
        console.error("[getLicensingTiers] Non-SystemAdmin available tiers API error:", error);
        throw error;
      }
    }

    try {
      const response = await apiRequest(buildApiUrl("/licensing/tiers"), { headers: getHeaders() });
      const data = await response.json();
      return data.map(t => ({
        ...t,
        name: t.name || t.displayName || '',
      }));
    } catch (error) {
      console.error("[getLicensingTiers] API error:", error);
      throw error;
    }
  });
};

export const createLicensingTier = async (tier) => {
  const apiPayload = mapTierToApiPayload(tier);
  try {
    const response = await apiRequest(buildApiUrl("/licensing/tiers"), {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(apiPayload),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("[createLicensingTier] API error:", error);
    throw error;
  }
};

export const updateLicensingTier = async (tierKey, tier) => {
  const apiPayload = mapTierToApiPayload(tier);
  try {
    const response = await apiRequest(buildApiUrl(`/licensing/tiers/${tierKey}`), {
      method: "PUT",
      headers: getHeaders(),
      body: JSON.stringify(apiPayload),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`[updateLicensingTier] API error for key '${tierKey}':`, error);
    throw error;
  }
};

export const updateLicensingTierStatus = async (tierKey, status) => {
  try {
    const response = await apiRequest(buildApiUrl(`/licensing/tiers/${tierKey}/status`), {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify({ status }),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`[updateLicensingTierStatus] API error for key '${tierKey}':`, error);
    throw error;
  }
};

export const deleteLicensingTier = async (tierKey) => {
  const response = await apiRequest(buildApiUrl(`/licensing/tiers/${tierKey}`), {
    method: "DELETE",
    headers: getHeaders(),
  });
  if (response.status === 204) {
    return { success: true };
  }
  return response.json();
};

// ==========================================
// 3. ORGANIZATION LICENSING ASSIGNMENTS & PROJECTIONS
// ==========================================

export const createLicensingAssignment = async (organizationId, payload) => {
  const response = await apiRequest(buildApiUrl(`/organizations/${organizationId}/licensing/assignments`), {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
  return response.json();
};

export const getCurrentLicensingAssignment = async (organizationId) => {
  return dedupeInFlight(`getCurrentLicensingAssignment:${organizationId}`, async () => {
    try {
      const response = await apiRequest(buildApiUrl(`/organizations/${organizationId}/licensing/assignments/current`), { headers: getHeaders() });
      if (!response.ok) {
        return null;
      }
      const data = await response.json();
      return data;
    } catch (err) {
      console.warn("[getCurrentLicensingAssignment] API error:", err);
      return null;
    }
  });
};

export const getLicensingAssignments = async (organizationId) => {
  return dedupeInFlight(`getLicensingAssignments:${organizationId}`, async () => {
    try {
      const response = await apiRequest(buildApiUrl(`/organizations/${organizationId}/licensing/assignments`), { headers: getHeaders() });
      if (!response.ok) {
        return [];
      }
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.warn("[getLicensingAssignments] API error:", err);
      return [];
    }
  });
};

export const getEntitlements = async (organizationId) => {
  return dedupeInFlight(`getEntitlements:${organizationId}`, async () => {
    const response = await apiRequest(buildApiUrl(`/organizations/${organizationId}/licensing/entitlements`), { headers: getHeaders() });
    const data = await response.json();
    if (!data) {
      return { enabledFeatures: [], limits: [] };
    }

    let currentAssignment = null;
    try {
      currentAssignment = await getCurrentLicensingAssignment(organizationId);
    } catch (err) {
      console.warn("[getEntitlements] Could not fetch current assignment:", err);
    }

    let resolvedTierName = data.tierName || data.displayName || currentAssignment?.tierName;
    if (!resolvedTierName && (data.tierKey || currentAssignment?.tierKey)) {
      try {
        const targetKey = data.tierKey || currentAssignment?.tierKey;
        const tiers = await getLicensingTiers();
        const matched = tiers.find(t => t.tierKey === targetKey);
        if (matched) {
          resolvedTierName = matched.name || matched.displayName;
        }
      } catch (err) {
        console.warn("[getEntitlements] Failed to fetch licensing tiers for name lookup:", err);
      }
    }

    const keyMap = {
      free: 'Free Plan',
      starter: 'Starter Plan',
      growth: 'Growth Plan',
      pro: 'Enterprise Pro',
      enterprise: 'Enterprise Plan'
    };

    const tierKeyToUse = data.tierKey || currentAssignment?.tierKey || '';
    if (!resolvedTierName && tierKeyToUse) {
      resolvedTierName = keyMap[tierKeyToUse] || (tierKeyToUse.charAt(0).toUpperCase() + tierKeyToUse.slice(1));
    }

    const enabledFeatures = Array.isArray(data.features)
      ? data.features.filter(f => f.isEnabled).map(f => f.featureKey)
      : (Array.isArray(data.enabledFeatures) ? data.enabledFeatures : []);

    const limits = Array.isArray(data.limits)
      ? data.limits.map(l => ({
        ...l,
        value: l.allowed !== undefined ? l.allowed : l.value,
        softLimitPercentage: l.softLimitPercentage !== undefined ? l.softLimitPercentage : 80,
        hardLimit: l.hardLimit !== undefined ? l.hardLimit : (l.allowed !== undefined ? l.allowed : l.value),
      }))
      : [];

    const effectiveFromUtc = currentAssignment?.effectiveFromUtc ||
      data.effectiveFromUtc ||
      currentAssignment?.assignedAtUtc ||
      data.assignedAtUtc ||
      null;

    const assignedAtUtc = currentAssignment?.assignedAtUtc ||
      data.assignedAtUtc ||
      effectiveFromUtc ||
      null;

    const effectiveToUtc = currentAssignment?.effectiveToUtc ||
      data.effectiveToUtc ||
      null;

    return {
      ...data,
      tierKey: tierKeyToUse,
      tierName: resolvedTierName || 'No Tier Assigned',
      enabledFeatures,
      limits,
      effectiveFromUtc,
      assignedAtUtc,
      effectiveToUtc,
      currentAssignment
    };
  });
};

export const getCurrentUsages = async (organizationId) => {
  const response = await apiRequest(buildApiUrl(`/organizations/${organizationId}/licensing/usages/current`), { headers: getHeaders() });
  return response.json();
};

// ==========================================
// 4. METERING PIPELINE
// ==========================================

export const getMeteringSummary = async (organizationId) => {
  const response = await apiRequest(buildApiUrl(`/organizations/${organizationId}/metering/summary`), { headers: getHeaders() });
  const data = await response.json();

  let records = [];
  if (data) {
    if (Array.isArray(data.dimensions)) {
      records = data.dimensions;
    } else if (Array.isArray(data.summary)) {
      records = data.summary;
    } else if (Array.isArray(data)) {
      records = data;
    }
  }

  // Map records to normalize property names (supporting PascalCase, camelCase, snake_case)
  const mappedRecords = records.map(rec => {
    if (!rec) return null;

    const id = rec.id || rec.Id || rec.meteringRecordId || rec.MeteringRecordId || rec.recordId || rec.RecordId || '';
    const organizationId = rec.organizationId || rec.OrganizationId || '';
    const segmentId = rec.segmentId || rec.SegmentId || '';
    const dimension = rec.dimension || rec.Dimension || '';
    const quantity = rec.quantity !== undefined ? rec.quantity : (rec.Quantity !== undefined ? rec.Quantity : 0);
    const confidence = rec.confidence || rec.Confidence || 'AggregateBacked';
    const isFinalized = rec.isFinalized !== undefined ? rec.isFinalized : (
      rec.IsFinalized !== undefined ? rec.IsFinalized : (
        rec.is_finalized !== undefined ? rec.is_finalized : false
      )
    );
    const billingCycleStartUtc = rec.billingCycleStartUtc || rec.BillingCycleStartUtc || '';
    const billingCycleEndUtc = rec.billingCycleEndUtc || rec.BillingCycleEndUtc || '';

    return {
      id,
      organizationId,
      segmentId,
      dimension,
      quantity: Number(quantity) || 0,
      confidence,
      isFinalized: Boolean(isFinalized),
      billingCycleStartUtc,
      billingCycleEndUtc
    };
  }).filter(Boolean);

  return {
    tierKey: data?.tierKey || data?.TierKey || '',
    billingCycleStartUtc: data?.billingCycleStartUtc || data?.BillingCycleStartUtc || '',
    billingCycleEndUtc: data?.billingCycleEndUtc || data?.BillingCycleEndUtc || '',
    records: mappedRecords
  };
};

export const getMeteringDaily = async (organizationId, params = {}) => {
  // Ensure we query with actual dates if parameters are missing, to avoid empty query strings
  let actualParams = { ...params };
  if (!actualParams.startDate || !actualParams.endDate) {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);
    actualParams.startDate = start.toISOString().split('T')[0];
    actualParams.endDate = end.toISOString().split('T')[0];
  }
  const search = new URLSearchParams(actualParams).toString();
  const response = await apiRequest(buildApiUrl(`/organizations/${organizationId}/metering/daily?${search}`), { headers: getHeaders() });
  const data = await response.json();

  let daysList = [];
  if (Array.isArray(data)) {
    daysList = data;
  } else if (data && Array.isArray(data.days)) {
    daysList = data.days;
  }

  // Check if it's nested dimensions list (e.g. { date, dimensions: [{ dimension, quantity }] })
  const hasNestedDimensions = daysList.length > 0 && daysList.some(item => item && Array.isArray(item.dimensions || item.Dimensions));

  // Check if it's flat aggregates (unpivoted format: [{ date, dimension, quantity }])
  const isFlatAggregate = !hasNestedDimensions && daysList.length > 0 && daysList.some(item => item && ('dimension' in item || 'Dimension' in item) && ('quantity' in item || 'Quantity' in item));

  if (hasNestedDimensions) {
    return daysList.map(item => {
      if (!item) return null;
      const date = item.date || item.Date || '';
      const dayObj = { date };

      const dims = item.dimensions || item.Dimensions || [];
      dims.forEach(d => {
        if (!d) return;
        const dimName = d.dimension || d.Dimension || '';
        if (!dimName) return;
        const qty = Number(d.quantity !== undefined ? d.quantity : (d.Quantity !== undefined ? d.Quantity : 0)) || 0;

        const normalizedName = dimName.toLowerCase().replace(/_/g, '');
        let targetKey = dimName.charAt(0).toLowerCase() + dimName.slice(1);
        if (normalizedName === 'consumersessions') {
          targetKey = 'consumerSessions';
        } else if (normalizedName === 'eventsingested') {
          targetKey = 'eventsIngested';
        } else if (normalizedName === 'apirequests') {
          targetKey = 'apiRequests';
        } else if (normalizedName === 'storage' || normalizedName === 'storagebytes') {
          targetKey = 'storageBytes';
        } else if (normalizedName === 'featureusage') {
          targetKey = 'featureUsage';
        }

        dayObj[targetKey] = qty;
      });

      return dayObj;
    }).filter(Boolean).sort((a, b) => a.date.localeCompare(b.date));
  } else if (isFlatAggregate) {
    // Group and pivot flat aggregates by date
    const dateGroups = {};
    for (const item of daysList) {
      if (!item) continue;
      const date = item.date || item.Date || '';
      if (!date) continue;

      if (!dateGroups[date]) {
        dateGroups[date] = {
          date,
          consumerSessions: 0,
          eventsIngested: 0,
          apiRequests: 0,
          storageBytes: 0
        };
      }

      const dim = (item.dimension || item.Dimension || '').toLowerCase().replace(/_/g, '');
      const qty = Number(item.quantity !== undefined ? item.quantity : (item.Quantity !== undefined ? item.Quantity : 0)) || 0;

      if (dim === 'consumersessions') {
        dateGroups[date].consumerSessions += qty;
      } else if (dim === 'eventsingested') {
        dateGroups[date].eventsIngested += qty;
      } else if (dim === 'apirequests') {
        dateGroups[date].apiRequests += qty;
      } else if (dim === 'storage' || dim === 'storagebytes') {
        dateGroups[date].storageBytes += qty;
      }
    }
    return Object.values(dateGroups).sort((a, b) => a.date.localeCompare(b.date));
  } else {
    // Map pivoted formats (camelCase, PascalCase, or snake_case)
    return daysList.map(item => {
      if (!item) return null;

      const date = item.date || item.Date || '';

      const consumerSessions = item.consumerSessions !== undefined ? item.consumerSessions : (
        item.consumer_sessions !== undefined ? item.consumer_sessions : (
          item.ConsumerSessions !== undefined ? item.ConsumerSessions : 0
        )
      );

      const eventsIngested = item.eventsIngested !== undefined ? item.eventsIngested : (
        item.events_ingested !== undefined ? item.events_ingested : (
          item.EventsIngested !== undefined ? item.EventsIngested : 0
        )
      );

      const apiRequests = item.apiRequests !== undefined ? item.apiRequests : (
        item.api_requests !== undefined ? item.api_requests : (
          item.ApiRequests !== undefined ? item.ApiRequests : 0
        )
      );

      const storageBytes = item.storageBytes !== undefined ? item.storageBytes : (
        item.storage_bytes !== undefined ? item.storage_bytes : (
          item.Storage !== undefined ? item.Storage : (
            item.storage !== undefined ? item.storage : 0
          )
        )
      );

      return {
        date,
        consumerSessions: Number(consumerSessions) || 0,
        eventsIngested: Number(eventsIngested) || 0,
        apiRequests: Number(apiRequests) || 0,
        storageBytes: Number(storageBytes) || 0
      };
    }).filter(Boolean).sort((a, b) => a.date.localeCompare(b.date));
  }
};

export const getMeteringCycleBreakdown = async (organizationId) => {
  const response = await apiRequest(buildApiUrl(`/organizations/${organizationId}/metering/cycle-breakdown`), { headers: getHeaders() });
  const data = await response.json();

  const now = new Date();
  const cycleAnchor = new Date();
  cycleAnchor.setDate(1);

  const billingCycleAnchorUtc = data?.billingCycleAnchorUtc || cycleAnchor.toISOString();
  let segments = Array.isArray(data?.segments) ? data.segments : [];

  const normalizedSegments = segments.map((seg) => {
    const segStart = seg.segmentStartUtc ? new Date(seg.segmentStartUtc) : now;
    const segEnd = seg.segmentEndUtc ? new Date(seg.segmentEndUtc) : now;

    const diffMs = Math.abs(segEnd - segStart);
    const calculatedDays = Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));

    const segmentDays = seg.segmentDays !== undefined && !isNaN(seg.segmentDays) ? seg.segmentDays : calculatedDays;
    const totalCycleDays = seg.totalCycleDays !== undefined && !isNaN(seg.totalCycleDays) ? seg.totalCycleDays : 30;
    const prorationFactor = seg.prorationFactor !== undefined && !isNaN(seg.prorationFactor) ? seg.prorationFactor : (segmentDays / totalCycleDays);

    return {
      ...seg,
      segmentDays,
      totalCycleDays,
      prorationFactor
    };
  });

  return {
    organizationId,
    billingCycleAnchorUtc,
    segments: normalizedSegments
  };
};

export const finalizeBillingCycle = async (organizationId, payload) => {
  const response = await apiRequest(buildApiUrl(`/organizations/${organizationId}/metering/finalize`), {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
  return response.json();
};

export const adjustMetering = async (organizationId, payload) => {
  const reasonCodeMapping = {
    'vendor-reconciliation': 'VendorReconciliation',
    'telemetry-outage': 'TelemetryOutage',
    'audit-correction': 'AuditCorrection'
  };
  const mappedReasonCode = reasonCodeMapping[payload.reasonCode] || payload.reasonCode;

  const apiPayload = {
    MeteringRecordId: payload.meteringRecordId || generateGuid(),
    QuantityDelta: Number(payload.quantityDelta),
    ReasonCode: mappedReasonCode,
    Notes: payload.notes || ""
  };

  const response = await apiRequest(buildApiUrl(`/organizations/${organizationId}/metering/adjust`), {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(apiPayload),
  });
  return response.json();
};

export const getRecordAdjustments = async (recordId) => {
  const response = await apiRequest(buildApiUrl(`/metering/records/${recordId}/adjustments`), { headers: getHeaders() });
  return response.json();
};

// ==========================================
// 5. RECONCILIATION
// ==========================================

export const runReconciliation = async (payload = {}) => {
  const response = await apiRequest(buildApiUrl("/policies/reconciliation/run"), {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  return {
    runKey: data?.runKey || '',
    startedAtUtc: data?.startedAtUtc || new Date().toISOString(),
    completedAtUtc: data?.completedAtUtc || new Date().toISOString(),
    organizationsEvaluated: data?.organizationsEvaluated !== undefined ? data.organizationsEvaluated : 1,
    organizationsWithDrift: data?.organizationsWithDrift !== undefined ? data.organizationsWithDrift : 0,
    budgetExceededOrganizations: data?.budgetExceededOrganizations !== undefined ? data.budgetExceededOrganizations : 0,
    partialFailure: Boolean(data?.partialFailure),
    ...data
  };
};

export const getReconciliationStatus = async (organizationId) => {
  const response = await apiRequest(buildApiUrl(`/organizations/${organizationId}/metering/reconciliation-status`), { headers: getHeaders() });
  const data = await response.json();
  return {
    organizationId: data?.organizationId || organizationId,
    status: data?.status || data?.reconciliationStatus || "Reconciled",
    confidence: data?.confidence || data?.confidenceLevel || "AggregateBacked",
    lastCheckedUtc: data?.lastCheckedUtc || data?.lastChecked || new Date().toISOString(),
    driftDetails: data?.driftDetails || data?.detail || "No drift detected across Raw Telemetry, Aggregates, and Projections.",
    driftCount: data?.driftCount !== undefined ? data.driftCount : (data?.organizationsWithDrift !== undefined ? data.organizationsWithDrift : 0),
    rawTelemetryTotal: data?.rawTelemetryTotal !== undefined ? data.rawTelemetryTotal : null,
    aggregateTotal: data?.aggregateTotal !== undefined ? data.aggregateTotal : null,
    projectionTotal: data?.projectionTotal !== undefined ? data.projectionTotal : null,
  };
};

export const getCrossTenantSummaries = async (fromUtc, toUtc) => {
  const params = {};
  if (fromUtc) params.fromUtc = fromUtc;
  if (toUtc) params.toUtc = toUtc;
  const response = await apiRequest(buildApiUrl("/metering/summaries", params), { headers: getHeaders() });
  return response.json();
};

// ==========================================
// 6. RETENTION POLICIES & LEGAL HOLDS
// ==========================================

export const getRetentionPolicies = async () => {
  const response = await apiRequest(buildApiUrl("/policies/retention-policies"), { headers: getHeaders() });
  const data = await response.json();
  const list = Array.isArray(data) ? data : [];
  return list.map(p => ({
    ...p,
    dryRun: p.dryRunEnabled !== undefined ? p.dryRunEnabled : p.dryRun
  }));
};

export const createRetentionPolicy = async (payload) => {
  const apiPayload = {
    policyKey: payload.policyKey,
    name: payload.name,
    retentionDays: Number(payload.retentionDays),
    maxDeleteBatchSize: Number(payload.maxDeleteBatchSize),
    dryRunEnabled: Boolean(payload.dryRun),
    resourceType: payload.resourceType
  };
  const response = await apiRequest(buildApiUrl("/policies/retention-policies"), {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(apiPayload),
  });
  const data = await response.json();
  return {
    ...data,
    dryRun: data.dryRunEnabled !== undefined ? data.dryRunEnabled : data.dryRun
  };
};

export const updateRetentionPolicy = async (policyKey, data) => {
  const apiPayload = {
    ...data,
    dryRunEnabled: data.dryRun !== undefined ? Boolean(data.dryRun) : undefined
  };
  delete apiPayload.dryRun;

  const response = await apiRequest(buildApiUrl(`/policies/retention-policies/${policyKey}`), {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify(apiPayload),
  });
  const resData = await response.json();
  return {
    ...resData,
    dryRun: resData.dryRunEnabled !== undefined ? resData.dryRunEnabled : resData.dryRun
  };
};

export const runRetentionExecution = async (policyKey) => {
  const response = await apiRequest(buildApiUrl(`/policies/retention-policies/${policyKey}/executions`), {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({}),
  });
  return response.json();
};

export const getRetentionExecutionHistory = async () => {
  const response = await apiRequest(buildApiUrl("/policies/retention-executions"), { headers: getHeaders() });
  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

export const getLegalHold = async (organizationId) => {
  try {
    const response = await apiRequest(buildApiUrl(`/policies/legal-holds?organizationId=${organizationId}`), { headers: getHeaders() });
    const data = await response.json();

    // If data is an array, find the active hold for this organization
    if (Array.isArray(data)) {
      const hold = data.find(h => h.organizationId === organizationId && (h.status === 'Active' || h.releasedAtUtc === null));
      return hold ? { ...hold, isActive: true } : { organizationId, isActive: false };
    }

    // If data is a single object
    if (data && typeof data === 'object') {
      const isActive = data.status === 'Active' || data.releasedAtUtc === null || data.isActive === true;
      return {
        ...data,
        isActive
      };
    }

    return { organizationId, isActive: false };
  } catch (err) {
    if (err.status === 404 || (err.message && (err.message.includes("404") || err.message.toLowerCase().includes("not found")))) {
      return { organizationId, isActive: false };
    }
    throw err;
  }
};

export const placeLegalHold = async (organizationId, payload) => {
  const response = await apiRequest(buildApiUrl("/policies/legal-holds"), {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({
      ...payload,
      organizationId
    }),
  });
  return response.json();
};

export const releaseLegalHold = async (organizationId, payload, legalHoldId) => {
  const targetHoldId = legalHoldId || payload.legalHoldId || payload.id || organizationId;
  const response = await apiRequest(buildApiUrl(`/policies/legal-holds/${targetHoldId}/release`), {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
  return response.json();
};

// ==========================================
// 7. QUOTA ENFORCEMENT & WARNING EVENTS
// ==========================================

export const getEnforcementEvents = async (organizationId) => {
  const response = await apiRequest(buildApiUrl(`/organizations/${organizationId}/licensing/enforcement-events`), { headers: getHeaders() });
  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

// ==========================================
// 8. MIGRATION READINESS AUDIT
// ==========================================

export const getMigrationReadiness = async (params = {}) => {
  const search = new URLSearchParams(params).toString();
  const response = await apiRequest(buildApiUrl(`/system/migration/legacy-analytics/retirement-readiness?${search}`), { headers: getHeaders() });
  return response.json();
};

// ==========================================
// 9. MODULE USAGE TELEMETRY
// ==========================================

export const getModuleUsage = async (organizationId, params = {}) => {
  const searchParams = new URLSearchParams();
  if (params.siteCollectionId) searchParams.append("siteCollectionId", params.siteCollectionId);
  if (params.fromUtc) searchParams.append("fromUtc", params.fromUtc);
  if (params.toUtc) searchParams.append("toUtc", params.toUtc);
  if (params.moduleKey) searchParams.append("moduleKey", params.moduleKey);
  const queryStr = searchParams.toString();
  const endpoint = `/telemetry/organizations/${encodeURIComponent(organizationId)}/module-usage${queryStr ? `?${queryStr}` : ""}`;
  const response = await apiRequest(buildApiUrl(endpoint), { headers: getHeaders() });
  const data = await response.json();
  return Array.isArray(data) ? data : [];
};